<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
    <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md mx-4 relative overflow-hidden">
      <!-- Close button -->
      <button
        class="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10"
        @click="$emit('close')"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- Header -->
      <div class="px-8 pt-8 pb-6 text-center">
        <div
          class="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {{ currentStep === 'email-sent' ? 'Check your email' : 'Welcome to TourScanner' }}
        </h2>
        <p class="text-gray-600 dark:text-gray-300 text-sm">
          {{
            currentStep === 'email-sent'
              ? 'We sent you a secure login link'
              : 'Get instant access to AI-powered travel planning'
          }}
        </p>
      </div>

      <!-- Content -->
      <div class="px-8 pb-8">
        <!-- Email sent state -->
        <div v-if="currentStep === 'email-sent'" class="text-center space-y-6">
          <div
            class="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center"
          >
            <svg
              class="w-10 h-10 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>

          <div class="space-y-3">
            <p class="text-gray-700 dark:text-gray-300">We sent a secure login link to:</p>
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-3">
              <p class="font-mono text-sm text-gray-800 dark:text-gray-200">{{ email }}</p>
            </div>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Click the link in your email to sign in. The link will expire in 10 minutes.
            </p>
          </div>

          <div class="space-y-3">
            <button
              @click="resendEmail"
              :disabled="loading || resendCooldown > 0"
              class="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 text-gray-700 dark:text-gray-200 rounded-xl font-medium transition-colors"
            >
              {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend email' }}
            </button>

            <button
              @click="goBack"
              class="w-full py-3 px-4 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 font-medium transition-colors"
            >
              Use different email
            </button>
          </div>
        </div>

        <!-- Main auth form -->
        <div v-else class="space-y-6">
          <!-- Social login buttons -->
          <div class="space-y-3">
            <button
              @click="handleOAuth('google')"
              :disabled="loading"
              class="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 bg-white dark:bg-gray-800"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span class="font-medium text-gray-700 dark:text-gray-200">Continue with Google</span>
            </button>
          </div>

          <!-- Divider -->
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-200 dark:border-gray-600"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="bg-white dark:bg-gray-800 px-4 text-gray-500 dark:text-gray-400"
                >or continue with email</span
              >
            </div>
          </div>

          <!-- Email form -->
          <form @submit.prevent="handleEmailAuth" class="space-y-4">
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email address
              </label>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                autocomplete="email"
                placeholder="Enter your email"
                class="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                :class="{ 'border-red-300 dark:border-red-500': error }"
              />
            </div>

            <button
              type="submit"
              :disabled="loading || !email"
              class="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-300 dark:disabled:from-gray-600 dark:disabled:to-gray-600 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed"
            >
              <div v-if="loading" class="flex items-center justify-center gap-2">
                <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Sending magic link...</span>
              </div>
              <span v-else>Send magic link</span>
            </button>
          </form>

          <!-- Error message -->
          <div
            v-if="error"
            class="p-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-700 rounded-xl"
          >
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
            </div>
          </div>

          <!-- Success message -->
          <div
            v-if="success"
            class="p-4 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-700 rounded-xl"
          >
            <div class="flex items-center gap-2">
              <svg
                class="w-5 h-5 text-green-500 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <p class="text-sm text-green-700 dark:text-green-300">{{ success }}</p>
            </div>
          </div>

          <!-- Features -->
          <div class="pt-4 border-t border-gray-100 dark:border-gray-700">
            <div class="grid grid-cols-2 gap-4 text-center">
              <div class="space-y-2">
                <div
                  class="w-10 h-10 mx-auto bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-blue-600 dark:text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <p class="text-xs text-gray-600 dark:text-gray-400 font-medium">AI Chat</p>
              </div>
              <div class="space-y-2">
                <div
                  class="w-10 h-10 mx-auto bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-purple-600 dark:text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <p class="text-xs text-gray-600 dark:text-gray-400 font-medium">Smart Recommendations</p>
              </div>
            </div>
          </div>

          <!-- Terms -->
          <p class="text-xs text-gray-500 dark:text-gray-400 text-center">
            By continuing, you agree to our
            <a href="/terms-of-service" class="text-blue-600 dark:text-blue-400 hover:underline">Terms of Service</a>
            and
            <a href="/privacy-policy" class="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { useAuth } from '~/composables/useAuth';

const { loginWithMagicLink, signInWithGoogle, signInWithApple, user } = useAuth();

const emit = defineEmits(['close', 'success']);

const props = defineProps({
  redirectTo: {
    type: String,
    default: null,
  },
});

// Reactive state
const email = ref('');
const loading = ref(false);
const error = ref('');
const success = ref('');
const currentStep = ref('form'); // 'form' | 'email-sent'
const resendCooldown = ref(0);

// Get runtime config to check environment
const runtimeConfig = useRuntimeConfig();
const isProduction = runtimeConfig.public.ENV_TYPE === 'production';

// Auto-focus email input when component mounts
onMounted(() => {
  const emailInput = document.getElementById('email');
  if (emailInput) {
    emailInput.focus();
  }
});

// Cleanup intervals
let resendInterval = null;
onUnmounted(() => {
  if (resendInterval) {
    clearInterval(resendInterval);
  }
});

// Handle magic link authentication
const handleEmailAuth = async () => {
  if (!email.value) return;

  error.value = '';
  success.value = '';
  loading.value = true;

  try {
    // Use the public site URL from config, fallback to current origin + /agent
    const defaultRedirectUrl = runtimeConfig.public.siteURL
      ? `${runtimeConfig.public.siteURL}/agent`
      : typeof window !== 'undefined'
      ? `${window.location.origin}/agent`
      : '/agent';

    const redirectUrl = props.redirectTo || defaultRedirectUrl;

    const { data, error: authError } = await loginWithMagicLink(email.value, {
      redirectTo: redirectUrl,
    });

    if (authError) throw authError;

    currentStep.value = 'email-sent';
    startResendCooldown();
  } catch (e) {
    error.value = e.message || 'Failed to send magic link. Please try again.';
  } finally {
    loading.value = false;
  }
};

// Handle OAuth authentication
const handleOAuth = async (provider) => {
  error.value = '';
  loading.value = true;

  try {
    // Use the public site URL from config, fallback to current origin + /agent
    const defaultRedirectUrl = runtimeConfig.public.siteURL
      ? `${runtimeConfig.public.siteURL}/agent`
      : typeof window !== 'undefined'
      ? `${window.location.origin}/agent`
      : '/agent';

    const redirectUrl = props.redirectTo || defaultRedirectUrl;

    let result;
    if (provider === 'google') {
      // For production, we want to avoid opening new windows
      // Supabase OAuth will handle the redirect properly
      result = await signInWithGoogle({
        redirectTo: redirectUrl,
      });
    } else if (provider === 'apple') {
      result = await signInWithApple({
        redirectTo: redirectUrl,
      });
    }

    if (result && result.error) throw result.error;

    // OAuth will redirect, so we don't need to do anything else here
    // In production, this should redirect in the same window
  } catch (e) {
    error.value = e.message || `${provider} sign-in failed. Please try again.`;
  } finally {
    loading.value = false;
  }
};

// Resend email functionality
const resendEmail = async () => {
  if (resendCooldown.value > 0) return;

  await handleEmailAuth();
};

const startResendCooldown = () => {
  resendCooldown.value = 30;
  resendInterval = setInterval(() => {
    resendCooldown.value--;
    if (resendCooldown.value <= 0) {
      clearInterval(resendInterval);
      resendInterval = null;
    }
  }, 1000);
};

// Go back to form
const goBack = () => {
  currentStep.value = 'form';
  error.value = '';
  success.value = '';
  if (resendInterval) {
    clearInterval(resendInterval);
    resendInterval = null;
  }
  resendCooldown.value = 0;
};

// Handle escape key
const handleEscape = (e) => {
  if (e.key === 'Escape') {
    emit('close');
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape);
});
</script>

<style scoped>
/* Custom animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.fixed {
  animation: fadeIn 0.2s ease-out;
}
</style>
